import React, { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { LanguageCode, translation } from './translations';

// ------- Konfiguracja -------
const COOKIE_NAME = 'lang';
const DEFAULT_LANGUAGE: LanguageCode = 'en';
const SUPPORTED_LANGUAGES: LanguageCode[] = ['pl', 'en'];

// ------- Pomocnicze funkcje -------
/** Pobiera język z cookie lub z przeglądarki (np. 'pl' z 'pl-PL') */
function detectInitialLanguage(): LanguageCode {
  const cookieLang = Cookies.get(COOKIE_NAME) as LanguageCode | undefined;
  if (cookieLang && SUPPORTED_LANGUAGES.includes(cookieLang)) return cookieLang;

  const browserLang = (navigator.language ?? DEFAULT_LANGUAGE).split('-')[0] as LanguageCode;
  return SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : DEFAULT_LANGUAGE;
}

// ------- Kontekst -------
interface I18nContextValue {
  language: LanguageCode;
  setLanguage: (next: LanguageCode) => void;
  translation: typeof translation; // udostępniamy funkcję tłumaczeń
}

const I18nContext = createContext<I18nContextValue>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  translation,
});

/** Hook dla komponentów */
export const useI18n = () => useContext(I18nContext);

/** Provider owijający całą aplikację (dodamy go w gatsby-browser i gatsby-ssr) */
export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<LanguageCode>(DEFAULT_LANGUAGE);

  // Ustalamy język przy pierwszym renderze (tylko client‑side)
  useEffect(() => {
    const initial = detectInitialLanguage();
    setLanguageState(initial);
    Cookies.set(COOKIE_NAME, initial, { expires: 365 });
  }, []);

  /** Zmiana języka – aktualizuje stan i cookie */
  const setLanguage = (next: LanguageCode) => {
    setLanguageState(next);
    Cookies.set(COOKIE_NAME, next, { expires: 365 });
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, translation }}>
      {children}
    </I18nContext.Provider>
  );
};
